import { useEffect } from "react";
import { validatePhoneNumberLength } from "libphonenumber-js";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import { MiContentDrawer, MiDetailFields } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
  PREFERRED_CONTACT_METHOD_OPTIONS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { capitalize, getPhoneNumber } from "@miplatform/utils";
import { mdiContacts } from "@mdi/js";
import { useAddContactDrawerViewModel } from "./useAddContactDrawerViewModel";

const DEFAULT_CONTACT = {
  accountId: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  title: "",
  preferredContactMethod: "Phone",
  label: "",
  isPrimary: false,
  isVIP: false,
};

/**
 * A drawer component for adding a new contact.
 *
 * @param {Object} props - Component props.
 * @param {Function} props.onClose - Function to close the drawer.
 * @param {boolean} props.loading - Indicates if the component is in a loading state.
 * @param {Object} rest - Additional props passed to the `MiContentDrawer` component.
 *
 * @returns {JSX.Element} The rendered drawer component for adding a contact.
 *
 * @example
 * <AddContactDrawer
 *   onClose={handleCloseDrawer}
 *   loading={isLoading}
 * />
 */
export const AddContactDrawer = ({ onClose, loading, ...rest }) => {
  const { classes } = useStyles();
  const edit = useEdit({ ...DEFAULT_CONTACT });
  const { accounts, getAccounts, createContact } = useAddContactDrawerViewModel(
    { edit }
  );
  const contactFields = [
    {
      label: "Account",
      name: "accountId",
      fieldType: INPUT_TYPES.SELECT,
      required: true,
      options: accounts,
      width: "50%",
      validationRule: z.string().min(1, "Account is required"),
    },
    {
      label: "Title",
      name: "title",
      width: "50%",
    },
    {
      label: "First Name",
      name: "firstName",
      required: true,
      width: "50%",
      beforeOnChange: capitalize,
      validationRule: z.string().min(1, "First name is required"),
    },
    {
      label: "Last Name",
      name: "lastName",
      required: true,
      width: "50%",
      beforeOnChange: capitalize,
      validationRule: z.string().min(1, "Last name is required"),
    },
    {
      label: "Phone Number",
      name: "phone",
      value: getPhoneNumber(edit.getValue("phone")),
      required: true,
      width: "50%",
      handleChange: (e) => {
        if (
          validatePhoneNumberLength(getPhoneNumber(e.target.value), "US") ===
          "TOO_LONG"
        ) {
          return;
        }

        edit.update({
          phone: getPhoneNumber(e.target.value),
        });
      },
      validationRule: z
        .string()
        .min(1, "Phone number is required")
        .refine(
          (value) => validatePhoneNumberLength(value, "US") === undefined,
          {
            message: "Please enter a valid phone number",
          }
        ),
    },
    {
      label: "Email",
      name: "email",
      required: true,
      width: "50%",
      validationRule: z
        .string()
        .min(1, "Email is required")
        .email("Please enter a valid email address"),
    },
    {
      label: "Preferred Method",
      name: "preferredContactMethod",
      width: "11%",
      fieldType: INPUT_TYPES.SELECT,
      options: PREFERRED_CONTACT_METHOD_OPTIONS,
      validationRule: z.string().min(1, "Preferred contact method is required"),
    },
    {
      label: "VIP",
      name: "isVIP",
      fieldType: INPUT_TYPES.CHECKBOX,
      width: "auto",
      validationRule: z.boolean().optional(),
    },
  ];

  edit.setValidation(contactFields);

  const handleDrawerClose = () => {
    edit.reset();
    onClose();
  };

  useEffect(() => {
    getAccounts();
  }, []);

  return (
    <MiContentDrawer
      variant="temporary"
      title="Add Contact"
      onClose={handleDrawerClose}
      leftIcon={mdiContacts}
      isLoading={loading}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          onClick: handleDrawerClose,
          color: BUTTON_COLORS.BLACK,
        },
        {
          name: "Submit",
          variant: BUTTON_VARIANTS.CONTAINED,
          color: BUTTON_COLORS.PRIMARY,
          size: "medium",
          disabled: !edit.isValid() || loading,
          onClick: createContact,
        },
      ]}
      {...rest}
    >
      <MiDetailFields
        detailFields={contactFields}
        edit={edit}
        customWrapperStyle={classes.container}
      />
    </MiContentDrawer>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: theme.spacing(3),
  },
}));
